import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

/**
 * TabbedSection — Reusable tabbed interface for grouping related content.
 * 
 * Props:
 *   tabs: [{ id: string, label: string, content: ReactNode }]
 *   defaultTab?: string (id of default active tab, defaults to first)
 *   className?: string (wrapper className)
 *   tabListClassName?: string (tab bar className)
 *   testId?: string (data-testid prefix)
 */
const TabbedSection = ({ tabs, defaultTab, className = '', tabListClassName = '', testId = 'tabbed-section' }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || (tabs[0] && tabs[0].id));

  if (!tabs || tabs.length === 0) return null;

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className={className} data-testid={testId}>
      <TabsList
        className={`inline-flex w-full sm:w-auto flex-wrap gap-1 bg-slate-100 border border-slate-200 rounded-xl p-1.5 ${tabListClassName}`}
        data-testid={`${testId}-tabs-list`}
      >
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            className="px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-[#0077FF] data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-[#0077FF]/20 text-slate-500 hover:text-slate-700"
            data-testid={`${testId}-tab-${tab.id}`}
          >
            {tab.icon && <tab.icon className="w-4 h-4 mr-1.5" />}
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((tab) => (
        <TabsContent key={tab.id} value={tab.id} className="mt-0 outline-none" data-testid={`${testId}-content-${tab.id}`}>
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default TabbedSection;
